import CssBaseline from "@mui/material/CssBaseline";
import Stack from "@mui/material/Stack";
import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../App";
import { AUTH_LOGIN_URL } from "../../Constants/Constants";
import {
  JWT_TOKEN,
  USER_EMAIL_ID,
  USER_ID,
  USER_NAME,
  USER_ORG_ID,
} from "../../Constants/StorageConstants";
import AxiosInstance from "../../services/AxiosInstance";
import AppTheme from "../v2/shared-theme/AppTheme";
import ColorModeIconDropdown from "../v2/shared-theme/ColorModeIconDropdown";
import MfaDialog from "./MfaDialog";
import OtpDialog from "./OtpDialog";
import SignInCard from "./SignInCard";
import SignInContent from "./SignInContent";

function Login({ setToken }) {
  const [formData, setFormData] = useState({ username: "", password: "" });
  const [apiError, setApiError] = useState(null);
  const navigate = useNavigate();
  const { updateAuthStatus } = useContext(AuthContext);

  const [showMfaDialog, setShowMfaDialog] = useState(false);
  const [showOtpDialog, setShowOtpDialog] = useState(false);
  const [tempToken, setTempToken] = useState("");
  const [qrUrl, setQrUrl] = useState("");

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError(null);

    localStorage.removeItem(JWT_TOKEN);
    localStorage.removeItem(USER_NAME);
    localStorage.removeItem(USER_ID);
    localStorage.removeItem(USER_ORG_ID);
    localStorage.removeItem(USER_EMAIL_ID);
    try {
      const response = await AxiosInstance.post(AUTH_LOGIN_URL, {
        id: formData.username,
        password: formData.password,
      });
      const userData = response.data;
      if (response.data.mfa_required && !response.data.qr_Enable) {
        setTempToken(response.data.token); // mfa=false JWT
        setShowOtpDialog(true);
      } else if (response.data.qr_Enable) {
        const token = response.data.token;
        setTempToken(token);

        const mfaRes = await AxiosInstance.post(
          "/api/auth/enable-mfa",
          { id: formData.username, password: formData.password },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        setQrUrl(mfaRes.data.qr_url);
        setShowMfaDialog(true);
      } else {
        setTempToken(response.data.token);
        localStorage.setItem(JWT_TOKEN, userData.token);
        localStorage.setItem(USER_NAME, userData.name);
        localStorage.setItem(USER_ID, userData.id);
        localStorage.setItem(USER_ORG_ID, userData.orgId);
        localStorage.setItem(USER_EMAIL_ID, userData.emailId);
        updateAuthStatus();
        navigate("/home");
      }
    } catch (error) {
      localStorage.removeItem(JWT_TOKEN);
      localStorage.removeItem(USER_NAME);
      localStorage.removeItem(USER_ID);
      localStorage.removeItem(USER_ORG_ID);
      localStorage.removeItem(USER_EMAIL_ID);
      if (error.response) {
        setApiError("Login failed. Invalid credentials or server error.");
      }
    }
  };

  const handleSSOLogin = () => {
    window.location.href =
      "https://your-sso-provider.com/auth?redirect_uri=http://your-app.com/callback";
  };

  return (
    <AppTheme>
      <CssBaseline enableColorScheme />
      {/* <ColorModeSelect sx={{ position: "fixed", top: "1rem", right: "1rem" }} /> */}
      <ColorModeIconDropdown
        sx={{ position: "fixed", top: "1rem", right: "1rem" }}
      />
      <Stack
        direction="column"
        component="main"
        sx={[
          {
            justifyContent: "center",
          },
          (theme) => ({
            "&::before": {
              content: '""',
              display: "block",
              position: "absolute",
              zIndex: -1,
              inset: 0,
              backgroundImage:
                "radial-gradient(ellipse at 50% 50%, hsl(210, 100%, 97%), hsl(0, 0%, 100%))",
              backgroundRepeat: "no-repeat",
              ...theme.applyStyles("dark", {
                backgroundImage:
                  "radial-gradient(at 50% 50%, hsla(210, 100%, 16%, 0.5), hsl(220, 30%, 5%))",
              }),
            },
          }),
        ]}
      >
        <Stack
          direction={{ xs: "column-reverse", md: "row" }}
          sx={{
            display: "flex",
            justifyContent: "center", // Centers items vertically
            alignItems: "center", // Centers items horizontally
            gap: { xs: 6, sm: 12 },
            mx: "auto",
            height: "100vh", // Ensures the stack takes the full viewport height
          }}
        >
          <Stack
            direction={{ xs: "column-reverse", md: "row" }}
            sx={{
              justifyContent: "center",
              gap: { xs: 6, sm: 12 },
              p: { xs: 2, sm: 4 },
              m: "auto",
            }}
          >
            <SignInContent />
            <SignInCard
              handleSubmit={handleSubmit}
              handleChange={handleChange}
              formData={formData}
              apiError={apiError}
              setApiError={setApiError}
            />
          </Stack>
        </Stack>
      </Stack>

      <MfaDialog
        open={showMfaDialog}
        onClose={() => setShowMfaDialog(false)}
        qrUrl={qrUrl}
        tempToken={tempToken}
        onSuccess={() => {
          setShowMfaDialog(false);
          setQrUrl("");
          setTempToken("");
        }}
      />

      <OtpDialog
        open={showOtpDialog}
        onClose={() => setShowOtpDialog(false)}
        tempToken={tempToken}
        onSuccess={() => {
          setShowOtpDialog(false);
          setTempToken("");
        }}
      />
    </AppTheme>
  );
}

export default Login;
